import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";

const KEY = "lm_cookie_consent";

export const getConsent = () => {
  try {
    return JSON.parse(localStorage.getItem(KEY));
  } catch {
    return null;
  }
};

// gtag.js is loaded in public/index.html on every page with Consent Mode v2 defaults (denied).
// Here we only flip the consent signal; Google keeps analytics cookies off until this says "granted".
const setAnalyticsConsent = (granted) => {
  if (typeof window.gtag !== "function") return;
  window.gtag("consent", "update", { analytics_storage: granted ? "granted" : "denied" });
};

export const openCookieSettings = () => window.dispatchEvent(new Event("lm:cookie-settings"));

export const CookieConsent = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const existing = getConsent();
    if (!existing) setOpen(true);
    else setAnalyticsConsent(!!existing.analytics);
    const reopen = () => setOpen(true);
    window.addEventListener("lm:cookie-settings", reopen);
    return () => window.removeEventListener("lm:cookie-settings", reopen);
  }, []);

  const choose = (analytics) => {
    localStorage.setItem(KEY, JSON.stringify({ analytics, at: new Date().toISOString() }));
    setAnalyticsConsent(analytics);
    setOpen(false);
    window.dispatchEvent(new Event("lm:consent"));
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie notice"
      data-testid="cookie-banner"
      className="fixed z-[60] inset-x-4 bottom-4 sm:inset-x-auto sm:right-6 sm:bottom-6 sm:max-w-md rounded-2xl border border-slate-200 bg-white shadow-[0_20px_60px_-15px_rgba(15,23,42,0.35)] p-5 animate-fade-up"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-cyan-50 border border-cyan-200 flex items-center justify-center shrink-0">
          <Cookie className="w-5 h-5 text-cyan-700" />
        </div>
        <div className="min-w-0">
          <h2 className="text-base font-bold text-slate-900">We keep cookies to a minimum</h2>
          <p className="mt-1 text-sm text-slate-600 leading-relaxed">
            This site uses essential browser storage only. If we add anonymous analytics later, it will run only if you
            accept. <Link to="/cookie-policy" data-testid="cookie-policy-link" className="text-cyan-700 font-medium hover:underline">Cookie Policy</Link>
          </p>
        </div>
      </div>
      <div className="mt-4 flex flex-col sm:flex-row gap-2 sm:justify-end">
        <button
          type="button"
          data-testid="cookie-decline"
          onClick={() => choose(false)}
          className="px-4 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-800 text-sm font-semibold hover:bg-slate-50 transition-colors"
        >
          Essential only
        </button>
        <button
          type="button"
          data-testid="cookie-accept"
          onClick={() => choose(true)}
          className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-cyan-600 to-teal-600 text-white text-sm font-bold hover:from-cyan-500 hover:to-teal-500 transition-colors"
        >
          Accept
        </button>
      </div>
    </div>
  );
};
